import { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";
import { Plus, X, Sparkles, Upload, CheckCircle2 } from "lucide-react";

const UNITS = [["day", "Full day"], ["half-day", "Half day"], ["hour", "Hour"], ["session", "Session"], ["other", "Other"]];
const money = (x) => (x == null ? "—" : `$${Number(x).toLocaleString(undefined, { minimumFractionDigits: 2 })}`);

const EMPTY = { name: "", school_ids: [], reference: "", po: "", start_date: "", end_date: "",
  authorized_amount: "", services: [], bill_to: "", billing_contact: "", terms_days: 30, notes: "", external_url: "" };

export default function Contracts() {
  const [list, setList] = useState(null);
  const [schools, setSchools] = useState([]);
  const [form, setForm] = useState(null); // null = closed, {id?...} = editing
  const [busy, setBusy] = useState("");

  const load = () => {
    api.get("/contracts").then((r) => setList(r.data)).catch((e) => toast.error(apiError(e)));
    api.get("/organizations").then((r) => setSchools(r.data.flatMap((o) => (o.schools || []).map((s) => ({ id: s.id, name: s.name }))))).catch(() => {});
  };
  useEffect(load, []);

  const save = async () => {
    setBusy("save");
    try {
      const payload = { ...form, authorized_amount: form.authorized_amount === "" ? null : Number(form.authorized_amount) };
      if (form.id) await api.patch(`/contracts/${form.id}`, payload);
      else await api.post("/contracts", payload);
      toast.success("Contract saved. Confirmed contracts control billing; drafts do not.");
      setForm(null); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const setStatus = async (c, status) => {
    try { await api.post(`/contracts/${c.id}/status`, { status }); load(); toast.success(`Contract ${status}.`); }
    catch (e) { toast.error(apiError(e)); }
  };

  const upload = async (c, e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const fd = new FormData(); fd.append("file", f);
      await api.post(`/contracts/${c.id}/document`, fd);
      toast.success("Document uploaded."); load();
    } catch (err) { toast.error(apiError(err)); } finally { e.target.value = ""; }
  };

  const extract = async (c) => {
    setBusy("extract");
    try {
      const { data } = await api.post(`/contracts/${c.id}/extract`, {});
      setForm({ ...EMPTY, ...c, id: c.id,
        reference: data.reference || c.reference, po: data.po || c.po,
        start_date: data.start_date || c.start_date, end_date: data.end_date || c.end_date,
        authorized_amount: data.authorized_amount ?? c.authorized_amount ?? "",
        services: data.services?.length ? data.services : c.services,
        bill_to: data.bill_to || c.bill_to, billing_contact: data.billing_contact || c.billing_contact,
        terms_days: data.terms_days || c.terms_days,
        extraction_notes: data.confidence_notes || [], extracted: true });
      toast("Extracted values loaded into the form for your review — nothing controls billing until you save.");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const setSvc = (i, fields) => setForm({ ...form, services: form.services.map((s, j) => (j === i ? { ...s, ...fields } : s)) });

  if (list === null) return <div className="text-sm text-[#78716C] animate-pulse" data-testid="contracts-loading">Loading contracts…</div>;

  return (
    <div className="space-y-6 max-w-6xl" data-testid="contracts-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow">Admin</p>
          <h1 className="section-title text-3xl mt-1">Contracts &amp; Purchase Orders</h1>
          <p className="text-sm text-[#57534E] mt-1 max-w-2xl">Confirmed contracts control billing: approved visit logs are invoiced only against a confirmed contract, at its rates, within its dates, up to the authorized amount.</p>
        </div>
        <button className="btn-primary" onClick={() => setForm({ ...EMPTY })} data-testid="new-contract-button"><Plus size={15} /> New contract</button>
      </div>

      {form && (
        <div className="card p-6 space-y-4 border-2 border-navy/10" data-testid="contract-form">
          <h2 className="font-serif font-bold text-lg text-navy">{form.id ? "Edit contract" : "New contract"}</h2>
          {form.extracted && (
            <div className="rounded-lg border border-[#FCD34D] bg-[#FFFBEB] px-4 py-3 text-xs text-[#92400E]" data-testid="extraction-review-note">
              AI-extracted values are loaded below for review. {(form.extraction_notes || []).length > 0 && <>Notes: {form.extraction_notes.join(" · ")}</>} Save only after checking every value against the document.
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className="label">Contract name</label><input className="input" value={form.name} data-testid="contract-name" onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><label className="label">Contract / PO reference</label><input className="input" value={form.reference} data-testid="contract-reference" onChange={(e) => setForm({ ...form, reference: e.target.value })} placeholder="e.g. Contract #, PO #" /></div>
            <div><label className="label">PO number</label><input className="input" value={form.po} data-testid="contract-po" onChange={(e) => setForm({ ...form, po: e.target.value })} /></div>
            <div><label className="label">Authorized amount ($)</label><input type="number" min="0" className="input" value={form.authorized_amount} data-testid="contract-authorized" onChange={(e) => setForm({ ...form, authorized_amount: e.target.value })} /></div>
            <div><label className="label">Effective start</label><input type="date" className="input" value={form.start_date} data-testid="contract-start" onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
            <div><label className="label">Effective end</label><input type="date" className="input" value={form.end_date} data-testid="contract-end" onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
            <div><label className="label">Payment terms (days)</label><input type="number" min="0" className="input" value={form.terms_days} data-testid="contract-terms" onChange={(e) => setForm({ ...form, terms_days: e.target.value })} /></div>
            <div><label className="label">Billing contact (name &amp; email)</label><input className="input" value={form.billing_contact} data-testid="contract-billing-contact" onChange={(e) => setForm({ ...form, billing_contact: e.target.value })} /></div>
            <div className="sm:col-span-2"><label className="label">Bill-to address</label><textarea className="input min-h-[60px]" value={form.bill_to} data-testid="contract-bill-to" onChange={(e) => setForm({ ...form, bill_to: e.target.value })} /></div>
            <div className="sm:col-span-2"><label className="label">Document link (https, e.g. Google Drive)</label><input className="input" value={form.external_url} data-testid="contract-url" onChange={(e) => setForm({ ...form, external_url: e.target.value })} /></div>
          </div>
          <div>
            <label className="label">Schools covered</label>
            <div className="flex flex-wrap gap-2" data-testid="contract-schools">
              {schools.map((s) => (
                <label key={s.id} className={`text-xs px-3 py-1.5 rounded-full border cursor-pointer font-bold ${form.school_ids.includes(s.id) ? "bg-navy text-white border-navy" : "bg-white text-navy border-[#E7E1D4]"}`} data-testid={`contract-school-${s.id}`}>
                  <input type="checkbox" className="hidden" checked={form.school_ids.includes(s.id)}
                    onChange={(e) => setForm({ ...form, school_ids: e.target.checked ? [...form.school_ids, s.id] : form.school_ids.filter((x) => x !== s.id) })} />
                  {s.name}
                </label>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="label !mb-0">Services &amp; rates <span className="font-normal text-[#78716C]">(multiple rates under one contract are supported)</span></label>
              <button className="btn-ghost text-xs" onClick={() => setForm({ ...form, services: [...form.services, { label: "", unit: "day", rate: "" }] })} data-testid="add-service"><Plus size={13} /> Add service</button>
            </div>
            {form.services.map((s, i) => (
              <div key={i} className="flex flex-wrap gap-2 items-center" data-testid={`service-edit-${i}`}>
                <input className="input !py-1.5 text-sm flex-1 min-w-[180px]" placeholder="Service label (e.g. Full-day coaching)" value={s.label} data-testid={`service-label-${i}`} onChange={(e) => setSvc(i, { label: e.target.value })} />
                <select className="input !py-1.5 text-sm" value={s.unit} data-testid={`service-unit-${i}`} onChange={(e) => setSvc(i, { unit: e.target.value })}>
                  {UNITS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <input type="number" min="0" className="input !py-1.5 text-sm !w-32" placeholder="Rate $" value={s.rate ?? ""} data-testid={`service-rate-${i}`} onChange={(e) => setSvc(i, { rate: e.target.value === "" ? null : Number(e.target.value) })} />
                <button className="btn-ghost !py-1 text-[#991B1B]" onClick={() => setForm({ ...form, services: form.services.filter((_, j) => j !== i) })} data-testid={`service-remove-${i}`}><X size={13} /></button>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            <button className="btn-primary" onClick={save} disabled={busy === "save" || !form.name.trim()} data-testid="contract-save"><CheckCircle2 size={15} /> Save (reviewed)</button>
            <button className="btn-ghost" onClick={() => setForm(null)} data-testid="contract-cancel">Cancel</button>
          </div>
        </div>
      )}

      <div className="card overflow-hidden" data-testid="contracts-table">
        <table className="w-full">
          <thead className="bg-sand/60"><tr>
            <th className="table-th">Contract</th><th className="table-th">Schools</th><th className="table-th">Dates</th>
            <th className="table-th">Services &amp; rates</th><th className="table-th">PO balance</th><th className="table-th">Status</th><th className="table-th"></th>
          </tr></thead>
          <tbody>
            {list.map((c) => (
              <tr key={c.id} data-testid={`contract-row-${c.id}`}>
                <td className="table-td">
                  <div className="font-bold text-navy">{c.name}</div>
                  <div className="text-xs text-[#78716C]">{[c.reference, c.po && `PO ${c.po}`].filter(Boolean).join(" · ")}</div>
                </td>
                <td className="table-td text-xs">{(c.school_names || []).join("; ") || "—"}</td>
                <td className="table-td text-xs">{c.start_date || "?"} → {c.end_date || "?"}</td>
                <td className="table-td text-xs">
                  {(c.services || []).map((s) => (
                    <div key={s.id}>{s.label}: <strong>{s.rate != null ? money(s.rate) : "no rate"}</strong>/{s.unit}</div>
                  )) || "—"}
                </td>
                <td className="table-td text-xs" data-testid={`po-balance-${c.id}`}>
                  {c.po_balance?.authorized != null ? (
                    <>Auth {money(c.po_balance.authorized)} · final {money(c.po_balance.final)} · drafts {money(c.po_balance.draft)} ·{" "}
                      <strong className={c.po_balance.remaining < 0 ? "text-[#991B1B]" : ""}>left {money(c.po_balance.remaining)}</strong></>
                  ) : "No authorized amount"}
                </td>
                <td className="table-td"><span className={c.status === "confirmed" ? "badge-approved" : c.status === "closed" ? "badge-nosend" : "badge-draft"} data-testid={`contract-status-${c.id}`}>{c.status}</span></td>
                <td className="table-td">
                  <div className="flex flex-wrap gap-1">
                    <button className="btn-ghost !py-1 !px-2 text-xs" onClick={() => setForm({ ...EMPTY, ...c })} data-testid={`contract-edit-${c.id}`}>Edit</button>
                    {c.status === "draft" && <button className="btn-outline !py-1 !px-2 text-xs" onClick={() => setStatus(c, "confirmed")} data-testid={`contract-confirm-${c.id}`}>Confirm</button>}
                    {c.status === "confirmed" && <button className="btn-ghost !py-1 !px-2 text-xs" onClick={() => setStatus(c, "closed")} data-testid={`contract-close-${c.id}`}>Close</button>}
                    <label className="btn-ghost !py-1 !px-2 text-xs cursor-pointer" data-testid={`contract-upload-${c.id}`}><Upload size={11} /> Doc
                      <input type="file" className="hidden" accept=".pdf,.docx,.txt" onChange={(e) => upload(c, e)} /></label>
                    {c.storage_path && <button className="btn-ghost !py-1 !px-2 text-xs" onClick={() => extract(c)} disabled={busy === "extract"} data-testid={`contract-extract-${c.id}`}><Sparkles size={11} /> {busy === "extract" ? "Extracting…" : "AI extract"}</button>}
                  </div>
                </td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan={7} className="table-td text-center text-[#78716C] py-10" data-testid="contracts-empty">No contracts yet. Create one, upload the contract or PO, and optionally use AI extract — values are reviewed before they control billing.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
